import React, {
  useEffect, useState, useCallback, Fragment, ReactElement,
} from 'react';
import Table from '../../../../../components/table/table.component';
import TableBody from '../../../../../components/table-body/table-body.component';
import TableRow from '../../../../../components/table-row/table-row.component';
import TableCell from '../../../../../components/table-cell/table-cell.component';
import FilteredLink from '../../../../../components/filtered-link/filtered-link.component';
import FormatService from '../../../../../services/format-service';
import DateService from '../../../../../services/date-service';
import StructureService from '../../../../../services/structure-service';
import GoogleMapService from '../../../../../services/google-map-service';
import {
  ENTITY_TYPE_TAG, ENTITY_TYPE_FILE, ENTITY_TYPE_ASSET, ENTITY_TYPE_ASSET_PHOTO, ENTITY_TYPE_ASSET_ALLOCATION_ITEM,
  ENTITY_TYPE_ASSET_SELLER, ENTITY_TYPE_ASSET_TYPE, ENTITY_TYPE_ASSET_TAG, ENTITY_TYPE_PORTFOLIO,
  ENTITY_TYPE_PORTFOLIO_MARKER_COLOR, ENTITY_TYPE_PORTFOLIO_GROUP, ENTITY_TYPE_ACTIVITY, ENTITY_TYPE_ACTIVITY_TAG,
  ENTITY_TYPE_ACTIVITY_DEAL_CONTACT, ENTITY_TYPE_CONTACT, ENTITY_TYPE_CONTACT_GROUP,
  ENTITY_TYPE_CONTACT_TO_CONTACT_GROUP, ENTITY_TYPE_CONTACT_ASSET_TYPE, ENTITY_TYPE_CONTACT_ADDRESS,
  ENTITY_TYPE_CONTACT_TAG, ENTITY_TYPE_DEAL, ENTITY_TYPE_DEAL_PHOTO, ENTITY_TYPE_DEAL_OPTIONS, ENTITY_TYPE_DEAL_SPONSOR,
  ENTITY_TYPE_DEAL_BROKER, ENTITY_TYPE_DEAL_LENDER, ENTITY_TYPE_DEAL_CONTACT, ENTITY_TYPE_DEAL_CONTACT_GROUP,
  ENTITY_TYPE_DEAL_MILESTONE, ENTITY_TYPE_DEAL_EXTERNAL_LINK, ENTITY_TYPE_DEAL_CAPITALIZATION,
  ENTITY_TYPE_DEAL_CAPITALIZATION_GROUP, ENTITY_TYPE_DEAL_CAPITALIZATION_ITEM,
  ENTITY_TYPE_DEAL_ASSET_USE_BREAKDOWN_ITEM, ENTITY_TYPE_DEAL_ASSET_USE_BREAKDOWN_PROPERTY,
  ENTITY_TYPE_DEAL_ASSET_USE_BREAKDOWN_HEADER, ENTITY_TYPE_DEAL_BUILDING_DETAILS, ENTITY_TYPE_DEAL_CUSTOM_SECTION_GROUP,
  ENTITY_TYPE_DEAL_CUSTOM_SECTION_FIELD, ENTITY_TYPE_DEAL_CUSTOM_LIST_TYPE, ENTITY_TYPE_DEAL_CUSTOM_LIST_TYPE_OPTION,
  ENTITY_TYPE_DEAL_TOP_LEVEL_METRICS_ITEM, ENTITY_TYPE_DEAL_INVESTMENT_RETURNS_ITEM,
  ENTITY_TYPE_DEAL_METRICS_STATISTICS_ITEM, ENTITY_TYPE_DEAL_METRICS_STATISTICS_GROUP,
  ENTITY_TYPE_DEAL_METRICS_CAPITAL_STACK_ITEM, ENTITY_TYPE_DEAL_HISTORICAL_VALUATIONS_ITEM,
  ENTITY_TYPE_DEAL_EQUITY_ORGANIZATION_CHART, ENTITY_TYPE_DEAL_TERM, ENTITY_TYPE_DEAL_TERM_GROUP, ENTITY_TYPE_DEAL_FILE,
  ENTITY_TYPE_DEAL_FILE_GROUP, ENTITY_TYPE_DEAL_TASK, ENTITY_TYPE_DEAL_TASK_GROUP, ENTITY_TYPE_DEAL_PARTICIPANT,
  ENTITY_TYPE_DEAL_PARTICIPANT_GROUP, ENTITY_TYPE_DEAL_SCHEDULE, ENTITY_TYPE_DEAL_SCHEDULE_TASK,
  ENTITY_TYPE_DEAL_SCHEDULE_TASK_LINK, ENTITY_TYPE_DEAL_TASK_REVIEW, ENTITY_TYPE_DEAL_TASK_CONTACT,
  ENTITY_TYPE_USER, ENTITY_TYPE_USER_ASSET_TYPE, ENTITY_TYPE_ACCOUNT_USER, ENTITY_TYPE_ACCOUNT_USER_DEAL,
  ENTITY_TYPE_ACCOUNT_USER_PORTFOLIO, ENTITY_TYPE_ACCOUNT_USER_DEAL_ACCESS_LEVEL,
} from '../../../../../constants/entity/entity-types';

const SettingsUserOperationFieldList: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const ENTITY_FIELD_NAME_ORIGINAL: string = 'original';
  const ENTITY_FIELD_NAME_RESULT: string = 'result';
  const COLUMNS_TOTAL: number = 2;

  const {
    isRoot,
    isDiff,
    isListItem,
    userOperationId,
    userOperationEntityFieldsData,
    userOperationNewEntityFieldsData,
    entityType,
    entityFieldNamePrev,
  } = props;

  const [visibleDataItems, setVisibleDataItems]: [number[], Function] = useState([]);

  const FIELD_NAME_ENTITY_MAP: Record<string, Record<string, string>> = {
    [ENTITY_TYPE_TAG]: {
      id: 'ID',
    },
    [ENTITY_TYPE_FILE]: {
      id: 'ID',
    },
    [ENTITY_TYPE_ASSET]: {
      id: 'ID',
      portfolioId: 'Portfolio ID',
      ltv: 'LTV',
      currentNoi: 'Current NOI',
      ttmNoi: 'TTM NOI',
      runRateNoi: 'Run Rate NOI',
      forwardNoi: 'Forward NOI',
      leveredIrr: 'Levered IRR',
      futureNoi: 'Future NOI',
      projectedStabilizedNoi: 'Projected Stabilized NOI',
      projectedUnleveredIrr: 'Projected Unlevered IRR',
      projectedLeveredIrr: 'Projected Levered IRR',
      seniorLoanLtv: 'Senior Loan LTV',
      seniorLoanLtc: 'Senior Loan LTC',
      mezzanineLoanLtv: 'Mezzanine Loan LTV',
      mezzanineLoanLtc: 'Mezzanine Loan LTC',
      yardiAssetId: 'Yardi Asset ID',
      yardiItdInterest: 'Yardi ITD Interest',
      yardiItdAmortization: 'Yardi ITD Amortization',
      yardiIrrOnLoan: 'Yardi IRR On Loan',
      yardiLtv: 'Yardi LTV',
      yardiCurrentSofr: 'Yardi Current SOFR',
      stabilizedAppraisedNoi: 'Stabilized Appraised NOI',
      atCloseNoi: 'At Close NOI',
      atCloseDebtYieldNoi: 'At Close Debt Yield NOI',
      atCloseDscrNoi: 'At Close DSCR NOI',
      atCloseNcf: 'At Close NCF',
      atCloseDebtYieldNcf: 'At Close Debt Yield NCF',
      atCloseDscrNcf: 'At Close DSCR NCF',
      atCloseNoiAndOccupancyDate: 'At Close NOI And Occupancy Date',
      stabilizedNoiAtOrigination: 'Stabilized NOI At Origination',
      stabilizedNcfAtOrigination: 'Stabilized NCF At Origination',
      createdByUserId: 'Created By User ID',
    },
    [ENTITY_TYPE_ASSET_PHOTO]: {
      id: 'ID',
    },
    [ENTITY_TYPE_ASSET_ALLOCATION_ITEM]: {
      id: 'ID',
      portfolioId: 'Portfolio ID',
    },
    [ENTITY_TYPE_ASSET_SELLER]: {
      id: 'ID',
      contactId: 'Contact ID',
    },
    [ENTITY_TYPE_ASSET_TAG]: {
      id: 'ID',
      tagId: 'Tag ID',
    },
    [ENTITY_TYPE_ASSET_TYPE]: {
      id: 'ID',
    },
    [ENTITY_TYPE_PORTFOLIO]: {
      id: 'ID',
    },
    [ENTITY_TYPE_PORTFOLIO_GROUP]: {
      id: 'ID',
    },
    [ENTITY_TYPE_PORTFOLIO_MARKER_COLOR]: {
      id: 'ID',
    },
    [ENTITY_TYPE_ACTIVITY]: {
      id: 'ID',
      oneYearNoi: 'One Year NOI',
      ltc: 'LTC',
      equityProviderId: 'Equity Provider ID',
      investorId: 'Investor ID',
      dealSourceId: 'Deal Source ID',
      dealId: 'Deal ID',
      sponsorId: 'Sponsor ID',
      brokerId: 'Broker ID',
      lenderId: 'Lender ID',
      additionalPartyId: 'Additional Party ID',
      createdByUserId: 'Created By User ID',
      updatedByUserId: 'Updated By User ID',
    },
    [ENTITY_TYPE_ACTIVITY_TAG]: {
      id: 'ID',
      tagId: 'Tag ID',
    },
    [ENTITY_TYPE_ACTIVITY_DEAL_CONTACT]: {
      id: 'ID',
      contactId: 'Contact ID',
    },
    [ENTITY_TYPE_CONTACT]: {
      id: 'ID',
      companyId: 'Company ID',
      userId: 'User ID',
    },
    [ENTITY_TYPE_CONTACT_TO_CONTACT_GROUP]: {
      id: 'ID',
      contactId: 'Contact ID',
    },
    [ENTITY_TYPE_CONTACT_ASSET_TYPE]: {
      id: 'ID',
      contactId: 'Contact ID',
    },
    [ENTITY_TYPE_CONTACT_ADDRESS]: {
      id: 'ID',
      contactId: 'Contact ID',
    },
    [ENTITY_TYPE_CONTACT_TAG]: {
      id: 'ID',
      tagId: 'Tag ID',
    },
    [ENTITY_TYPE_DEAL]: {
      id: 'ID',
      portfolioId: 'Portfolio ID',
      assetId: 'Asset ID',
      zfaAmount: 'ZFA Amount',
      gsfAmount: 'GSF Amount',
      nsfAmount: 'NSF Amount',
      lotsfAmount: 'Lot SF Amount',
      rfp: 'RFP',
      estimatedTdc: 'Estimated TDC',
      projectedNoiAmount: 'Projected NOI Amount',
      irrAmount: 'IRR Amount',
    },
    [ENTITY_TYPE_DEAL_PHOTO]: {
      id: 'ID',
      createdByUserId: 'Created By User ID',
    },
    [ENTITY_TYPE_DEAL_CONTACT_GROUP]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_CONTACT]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_MILESTONE]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_EXTERNAL_LINK]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_CUSTOM_SECTION_GROUP]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_CUSTOM_SECTION_FIELD]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_CUSTOM_LIST_TYPE]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_TOP_LEVEL_METRICS_ITEM]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_INVESTMENT_RETURNS_ITEM]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_METRICS_STATISTICS_GROUP]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_METRICS_STATISTICS_ITEM]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_METRICS_CAPITAL_STACK_ITEM]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_HISTORICAL_VALUATIONS_ITEM]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_EQUITY_ORGANIZATION_CHART]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_ASSET_USE_BREAKDOWN_ITEM]: {
      id: 'ID',
      assetTypeId: 'Asset Type ID',
    },
    [ENTITY_TYPE_DEAL_ASSET_USE_BREAKDOWN_HEADER]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_ASSET_USE_BREAKDOWN_PROPERTY]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_CAPITALIZATION_GROUP]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_CAPITALIZATION_ITEM]: {
      id: 'ID',
      zfaAmount: 'ZFA Amount',
      gsfAmount: 'GSF Amount',
      nsfAmount: 'NSF Amount',
      lotsfAmount: 'Lot SF Amount',
    },
    [ENTITY_TYPE_DEAL_SPONSOR]: {
      id: 'ID',
      contactId: 'Contact ID',
    },
    [ENTITY_TYPE_DEAL_BROKER]: {
      id: 'ID',
      contactId: 'Contact ID',
    },
    [ENTITY_TYPE_DEAL_LENDER]: {
      id: 'ID',
      contactId: 'Contact ID',
    },
    [ENTITY_TYPE_DEAL_TERM]: {
      id: 'ID',
      dealTermGroupId: 'Deal Term Group ID',
    },
    [ENTITY_TYPE_DEAL_TERM_GROUP]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_TASK]: {
      id: 'ID',
      parentId: 'Parent ID',
      createdByUserId: 'Created By User ID',
      updatedByUserId: 'Updated By User ID',
    },
    [ENTITY_TYPE_DEAL_TASK_GROUP]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_TASK_REVIEW]: {
      id: 'ID',
      contactId: 'Contact ID',
    },
    [ENTITY_TYPE_DEAL_TASK_CONTACT]: {
      id: 'ID',
      contactId: 'Contact ID',
    },
    [ENTITY_TYPE_DEAL_FILE]: {
      id: 'ID',
      fileId: 'File ID',
      createdByUserId: 'Created By User ID',
    },
    [ENTITY_TYPE_DEAL_FILE_GROUP]: {
      id: 'ID',
      dealId: 'Deal ID',
    },
    [ENTITY_TYPE_DEAL_PARTICIPANT]: {
      id: 'ID',
      contactId: 'Contact ID',
      dealParticipantGroupId: 'Deal Participant Group ID',
      createdByUserId: 'Created By User ID',
      updatedByUserId: 'Updated By User ID',
    },
    [ENTITY_TYPE_DEAL_PARTICIPANT_GROUP]: {
      id: 'ID',
      dealId: 'Deal ID',
    },
    [ENTITY_TYPE_DEAL_SCHEDULE]: {
      id: 'ID',
    },
    [ENTITY_TYPE_DEAL_SCHEDULE_TASK]: {
      id: 'ID',
      dealId: 'Deal ID',
    },
    [ENTITY_TYPE_DEAL_SCHEDULE_TASK_LINK]: {
      id: 'ID',
      dealId: 'Deal ID',
      sourceId: 'Source ID',
      targetId: 'Target ID',
    },
    [ENTITY_TYPE_USER]: {
      id: 'ID',
      apiKey: 'API Key',
      otpSecretData: 'OTP Secret Data',
    },
    [ENTITY_TYPE_USER_ASSET_TYPE]: {
      id: 'ID',
    },
    [ENTITY_TYPE_ACCOUNT_USER]: {
      id: 'ID',
    },
    [ENTITY_TYPE_ACCOUNT_USER_DEAL]: {
      id: 'ID',
      dealId: 'Deal ID',
    },
    [ENTITY_TYPE_ACCOUNT_USER_PORTFOLIO]: {
      id: 'ID',
      portfolioId: 'Portfolio ID',
    },
    [ENTITY_TYPE_ACCOUNT_USER_DEAL_ACCESS_LEVEL]: {
      id: 'ID',
      dealId: 'Deal ID',
    },
  };

  const ENTITY_TYPE_ENTITY_MAP: Record<string, Record<string, string>> = {
    [ENTITY_TYPE_ASSET]: {
      portfolio: ENTITY_TYPE_PORTFOLIO,
    },
    [ENTITY_TYPE_ASSET_TAG]: {
      tag: ENTITY_TYPE_TAG,
    },
    [ENTITY_TYPE_ASSET_SELLER]: {
      contact: ENTITY_TYPE_CONTACT,
    },
    [ENTITY_TYPE_PORTFOLIO]: {
      group: ENTITY_TYPE_PORTFOLIO_GROUP,
      portfolioMarkerColor: ENTITY_TYPE_PORTFOLIO_MARKER_COLOR,
    },
    [ENTITY_TYPE_ACTIVITY_TAG]: {
      tag: ENTITY_TYPE_TAG,
    },
    [ENTITY_TYPE_ACTIVITY_DEAL_CONTACT]: {
      contact: ENTITY_TYPE_CONTACT,
    },
    [ENTITY_TYPE_CONTACT]: {
      group: ENTITY_TYPE_CONTACT_TO_CONTACT_GROUP,
      company: ENTITY_TYPE_CONTACT,
    },
    [ENTITY_TYPE_CONTACT_TAG]: {
      tag: ENTITY_TYPE_TAG,
    },
    [ENTITY_TYPE_DEAL]: {
      options: ENTITY_TYPE_DEAL_OPTIONS,
    },
    [ENTITY_TYPE_DEAL_OPTIONS]: {
      dealBuildingDetails: ENTITY_TYPE_DEAL_BUILDING_DETAILS,
      dealCapitalization: ENTITY_TYPE_DEAL_CAPITALIZATION,
      dealEquityOrganizationChart: ENTITY_TYPE_DEAL_EQUITY_ORGANIZATION_CHART,
    },
    [ENTITY_TYPE_DEAL_SPONSOR]: {
      contact: ENTITY_TYPE_CONTACT,
    },
    [ENTITY_TYPE_DEAL_BROKER]: {
      contact: ENTITY_TYPE_CONTACT,
    },
    [ENTITY_TYPE_DEAL_LENDER]: {
      contact: ENTITY_TYPE_CONTACT,
    },
    [ENTITY_TYPE_DEAL_TERM]: {
      group: ENTITY_TYPE_DEAL_TERM_GROUP,
    },
    [ENTITY_TYPE_DEAL_TASK]: {
      group: ENTITY_TYPE_DEAL_TASK_GROUP,
      parentTask: ENTITY_TYPE_DEAL_TASK,
    },
    [ENTITY_TYPE_DEAL_TASK_REVIEW]: {
      contact: ENTITY_TYPE_CONTACT,
    },
    [ENTITY_TYPE_DEAL_TASK_CONTACT]: {
      contact: ENTITY_TYPE_CONTACT,
    },
    [ENTITY_TYPE_CONTACT_TO_CONTACT_GROUP]: {
      group: ENTITY_TYPE_CONTACT_GROUP,
    },
    [ENTITY_TYPE_DEAL_FILE]: {
      file: ENTITY_TYPE_FILE,
      group: ENTITY_TYPE_DEAL_FILE_GROUP,
    },
    [ENTITY_TYPE_DEAL_PARTICIPANT]: {
      group: ENTITY_TYPE_DEAL_PARTICIPANT_GROUP,
      contact: ENTITY_TYPE_CONTACT,
      ndaFile: ENTITY_TYPE_FILE,
    },
    [ENTITY_TYPE_ACCOUNT_USER]: {
      accountUserCompany: ENTITY_TYPE_CONTACT,
    },
    [ENTITY_TYPE_ACCOUNT_USER_DEAL]: {
      deal: ENTITY_TYPE_DEAL,
    },
    [ENTITY_TYPE_ACCOUNT_USER_PORTFOLIO]: {
      portfolio: ENTITY_TYPE_PORTFOLIO,
    },
  };

  const ENTITY_LIST_TYPE_ENTITY_MAP: Record<string, Record<string, string>> = {
    [ENTITY_TYPE_ASSET]: {
      photos: ENTITY_TYPE_ASSET_PHOTO,
      allocationItems: ENTITY_TYPE_ASSET_ALLOCATION_ITEM,
      sellers: ENTITY_TYPE_ASSET_SELLER,
      types: ENTITY_TYPE_ASSET_TYPE,
      tags: ENTITY_TYPE_ASSET_TAG,
    },
    [ENTITY_TYPE_ACTIVITY]: {
      tags: ENTITY_TYPE_ACTIVITY_TAG,
      dealContacts: ENTITY_TYPE_ACTIVITY_DEAL_CONTACT,
    },
    [ENTITY_TYPE_CONTACT]: {
      contactGroups: ENTITY_TYPE_CONTACT_TO_CONTACT_GROUP,
      contactAssetTypes: ENTITY_TYPE_CONTACT_ASSET_TYPE,
      contactAddresses: ENTITY_TYPE_CONTACT_ADDRESS,
      tags: ENTITY_TYPE_CONTACT_TAG,
    },
    [ENTITY_TYPE_DEAL]: {
      photos: ENTITY_TYPE_DEAL_PHOTO,
      sponsors: ENTITY_TYPE_DEAL_SPONSOR,
      brokers: ENTITY_TYPE_DEAL_BROKER,
      lenders: ENTITY_TYPE_DEAL_LENDER,
      fileGroups: ENTITY_TYPE_DEAL_FILE_GROUP,
      participantGroups: ENTITY_TYPE_DEAL_PARTICIPANT_GROUP,
    },
    [ENTITY_TYPE_DEAL_OPTIONS]: {
      dealContacts: ENTITY_TYPE_DEAL_CONTACT_GROUP,
      dealMilestones: ENTITY_TYPE_DEAL_MILESTONE,
      dealExternalLinks: ENTITY_TYPE_DEAL_EXTERNAL_LINK,
      dealCustomSections: ENTITY_TYPE_DEAL_CUSTOM_SECTION_GROUP,
      dealCustomListTypes: ENTITY_TYPE_DEAL_CUSTOM_LIST_TYPE,
      dealTopLevelMetrics: ENTITY_TYPE_DEAL_TOP_LEVEL_METRICS_ITEM,
      dealInvestmentReturns: ENTITY_TYPE_DEAL_INVESTMENT_RETURNS_ITEM,
      dealMetricsStatistics: ENTITY_TYPE_DEAL_METRICS_STATISTICS_GROUP,
      dealMetricsCapitalStack: ENTITY_TYPE_DEAL_METRICS_CAPITAL_STACK_ITEM,
      dealHistoricalValuations: ENTITY_TYPE_DEAL_HISTORICAL_VALUATIONS_ITEM,
    },
    [ENTITY_TYPE_DEAL_BUILDING_DETAILS]: {
      assetUseBreakdown: ENTITY_TYPE_DEAL_ASSET_USE_BREAKDOWN_ITEM,
      assetUseBreakdownHeaders: ENTITY_TYPE_DEAL_ASSET_USE_BREAKDOWN_HEADER,
    },
    [ENTITY_TYPE_DEAL_ASSET_USE_BREAKDOWN_ITEM]: {
      breakdownProperties: ENTITY_TYPE_DEAL_ASSET_USE_BREAKDOWN_PROPERTY,
    },
    [ENTITY_TYPE_DEAL_CONTACT_GROUP]: {
      contacts: ENTITY_TYPE_DEAL_CONTACT,
    },
    [ENTITY_TYPE_DEAL_CUSTOM_SECTION_GROUP]: {
      fields: ENTITY_TYPE_DEAL_CUSTOM_SECTION_FIELD,
    },
    [ENTITY_TYPE_DEAL_CUSTOM_LIST_TYPE]: {
      options: ENTITY_TYPE_DEAL_CUSTOM_LIST_TYPE_OPTION,
    },
    [ENTITY_TYPE_DEAL_METRICS_STATISTICS_GROUP]: {
      values: ENTITY_TYPE_DEAL_METRICS_STATISTICS_ITEM,
    },
    [ENTITY_TYPE_DEAL_CAPITALIZATION]: {
      sources: ENTITY_TYPE_DEAL_CAPITALIZATION_GROUP,
      uses: ENTITY_TYPE_DEAL_CAPITALIZATION_GROUP,
    },
    [ENTITY_TYPE_DEAL_CAPITALIZATION_GROUP]: {
      items: ENTITY_TYPE_DEAL_CAPITALIZATION_ITEM,
    },
    [ENTITY_TYPE_DEAL_TASK]: {
      reviews: ENTITY_TYPE_DEAL_TASK_REVIEW,
      taskContacts: ENTITY_TYPE_DEAL_TASK_CONTACT,
    },
    [ENTITY_TYPE_USER]: {
      userAssetTypes: ENTITY_TYPE_USER_ASSET_TYPE,
      accountUsers: ENTITY_TYPE_ACCOUNT_USER,
    },
    [ENTITY_TYPE_ACCOUNT_USER]: {
      accountUserDeals: ENTITY_TYPE_ACCOUNT_USER_DEAL,
      accountUserPortfolios: ENTITY_TYPE_ACCOUNT_USER_PORTFOLIO,
      accountUserDealAccessLevels: ENTITY_TYPE_ACCOUNT_USER_DEAL_ACCESS_LEVEL,
    },
  };

  const FLAT_KEY_ENTITY_LIST_MAP: Record<string, string[]> = {
    [ENTITY_TYPE_DEAL_OPTIONS]: [
      'dealBuildingDetails',
      'dealCapitalization',
    ],
  };

  const HIDDEN_FIELD_ENTITY_MAP: Record<string, string[]> = {
    [ENTITY_TYPE_FILE]: [
      'createdAt',
    ],
    [ENTITY_TYPE_ASSET]: [
      'assetStatus',
      'deals',
    ],
    [ENTITY_TYPE_ASSET_ALLOCATION_ITEM]: [
      'portfolio',
    ],
    [ENTITY_TYPE_ASSET_TYPE]: [
      'assetType',
    ],
    [ENTITY_TYPE_PORTFOLIO]: [
      'accountId',
      'dealConnectorCredentials',
    ],
    [ENTITY_TYPE_ACTIVITY]: [
      'accountId',
      'activityStatus',
      'activityCategory',
      'updatedByUser',
      'deal',
      'sponsor',
      'broker',
      'lender',
    ],
    [ENTITY_TYPE_CONTACT]: [
      'accountId',
      'isDefault',
      'contactType',
      'user',
      'comments',
      'contacts',
    ],
    [ENTITY_TYPE_CONTACT_ASSET_TYPE]: [
      'assetType',
    ],
    [ENTITY_TYPE_DEAL]: [
      'accountId',
      'dealType',
      'dealStatus',
      'asset',
      'activities',
      'tabSettings',
      'createdAt',
      'updatedAt',
    ],
    [ENTITY_TYPE_DEAL_SPONSOR]: [
      'dealId',
      'createdAt',
      'updatedAt',
    ],
    [ENTITY_TYPE_DEAL_BROKER]: [
      'dealId',
      'createdAt',
      'updatedAt',
    ],
    [ENTITY_TYPE_DEAL_LENDER]: [
      'dealId',
      'createdAt',
      'updatedAt',
    ],
    [ENTITY_TYPE_DEAL_PHOTO]: [
      'dealId',
      'createdAt',
      'updatedAt',
    ],
    [ENTITY_TYPE_DEAL_TERM_GROUP]: [
      'deal',
      'dealId',
      'createdAt',
      'updatedAt',
    ],
    [ENTITY_TYPE_DEAL_TERM]: [
      'createdAt',
      'updatedAt',
    ],
    [ENTITY_TYPE_DEAL_TASK]: [
      'createdAt',
      'dealTaskStatus',
      'comments',
    ],
    [ENTITY_TYPE_DEAL_TASK_GROUP]: [
      'deal',
    ],
    [ENTITY_TYPE_DEAL_TASK_REVIEW]: [
      'dealTaskReviewStatus',
    ],
    [ENTITY_TYPE_DEAL_TASK_CONTACT]: [
      'dealTaskId',
    ],
    [ENTITY_TYPE_DEAL_FILE_GROUP]: [
      'deal',
      'dealId',
      'createdAt',
      'updatedAt',
    ],
    [ENTITY_TYPE_DEAL_PARTICIPANT]: [
      'createdAt',
      'updatedAt',
      'dealParticipantAccessLevel',
    ],
    [ENTITY_TYPE_DEAL_PARTICIPANT_GROUP]: [
      'deal',
      'dealId',
      'createdAt',
      'updatedAt',
    ],
    [ENTITY_TYPE_DEAL_SCHEDULE]: [
      'deal',
    ],
    [ENTITY_TYPE_DEAL_SCHEDULE_TASK]: [
      'createdAt',
      'updatedAt',
    ],
    [ENTITY_TYPE_DEAL_SCHEDULE_TASK_LINK]: [
      'createdAt',
      'updatedAt',
    ],
    [ENTITY_TYPE_USER]: [
      'salt',
      'createdAt',
      'updatedAt',
      'userRole',
      'contacts',
      'userAccountOperations',
    ],
    [ENTITY_TYPE_USER_ASSET_TYPE]: [
      'assetType',
    ],
    [ENTITY_TYPE_ACCOUNT_USER]: [
      'account',
      'accountId',
      'createdByUser',
      'accountUserType',
    ],
    [ENTITY_TYPE_ACCOUNT_USER_DEAL]: [
      'accountUserId',
    ],
    [ENTITY_TYPE_ACCOUNT_USER_PORTFOLIO]: [
      'accountUserId',
    ],
    [ENTITY_TYPE_ACCOUNT_USER_DEAL_ACCESS_LEVEL]: [
      'accountUserId',
      'accountUserDealAccessLevel',
    ],
  };

  const DATE_VALUE_ENTITY_MAP: Record<string, string[]> = {
    [ENTITY_TYPE_ASSET]: [
      'maturityDate',
      'closeDate',
      'seniorLoanClosingDate',
      'seniorLoanMaturityDate',
      'mezzanineLoanClosingDate',
      'mezzanineLoanMaturityDate',
      'yardiLoanClose',
      'yardiLoanMaturity',
      'firstPaymentDate',
      'nextPaymentDueDate',
      'currentCreditLetterCreditExpirationDate',
      'initialAppraisalDate',
      'mostRecentAppraisalDate',
      'atCloseNoiAndOccupancyDate',
      'firstExtendedMaturityDate',
      'secondExtendedMaturityDate',
      'thirdExtendedMaturityDate',
      'initialMaturityDate',
    ],
    [ENTITY_TYPE_DEAL]: [
      'receivedAt',
      'closedAt',
      'bidAt',
    ],
    [ENTITY_TYPE_DEAL_MILESTONE]: [
      'value',
    ],
    [ENTITY_TYPE_DEAL_TASK]: [
      'dueAt',
    ],
    [ENTITY_TYPE_DEAL_SCHEDULE_TASK]: [
      'startedAt',
    ],
    [ENTITY_TYPE_USER]: [
      'lastLoginAt',
      'lastActiveAt',
    ],
  };

  const ADDRESS_VALUE_ENTITY_MAP: Record<string, string[]> = {
    [ENTITY_TYPE_ASSET]: [
      'address',
    ],
    [ENTITY_TYPE_USER]: [
      'address',
    ],
    [ENTITY_TYPE_CONTACT]: [
      'address',
    ],
    [ENTITY_TYPE_CONTACT_ADDRESS]: [
      'address',
    ],
  };

  const HTML_TEXT_VALUE_ENTITY_MAP: Record<string, string[]> = {
    [ENTITY_TYPE_DEAL]: [
      'description',
    ],
  };

  const HIDDEN_VALUE_ENTITY_MAP: Record<string, string[]> = {
    [ENTITY_TYPE_USER]: [
      'password',
      'apiKey',
      'otpSecretData',
    ],
  };

  const formatEntityFieldName: Function = useCallback((entityFieldName: string, entityFieldType: string): string => {
    const fieldNameMap: Record<string, string> | null = (entityFieldType
      && FIELD_NAME_ENTITY_MAP[entityFieldType]) || null;

    if (fieldNameMap && fieldNameMap[entityFieldName]) {
      return fieldNameMap[entityFieldName];
    }

    return FormatService.formatCamelCaseToWords(entityFieldName);
  }, []);

  const formatEntityFieldValue: Function = useCallback((
    entityFieldValue: StructureItem, entityFieldName: string, entityFieldType: string,
  ): ReactElement | string | null => {
    const entityTypeMap: Record<string, string> | null = (entityFieldType
      && ENTITY_TYPE_ENTITY_MAP[entityFieldType]) || null;
    const entityListTypeMap: Record<string, string> | null = (entityFieldType
      && ENTITY_LIST_TYPE_ENTITY_MAP[entityFieldType]) || null;

    if (!entityFieldValue && entityFieldValue !== 0) {
      return null;
    }

    const isDiffObject: boolean = entityFieldValue[ENTITY_FIELD_NAME_ORIGINAL] !== undefined
      || entityFieldValue[ENTITY_FIELD_NAME_RESULT] !== undefined;

    if (StructureService.checkIsValueArray(entityFieldValue)) {
      if (!entityFieldValue.length) {
        return null;
      }

      if (entityListTypeMap && entityListTypeMap[entityFieldName]) {
        return (
          <Fragment>
            {
              entityFieldValue.map((entityFieldItem: StructureItem, entityFieldItemIndex: number): ReactElement => {
                const isDiffItem: boolean = entityFieldItem[ENTITY_FIELD_NAME_ORIGINAL] !== undefined
                  || entityFieldItem[ENTITY_FIELD_NAME_RESULT] !== undefined;

                return (
                  <SettingsUserOperationFieldList
                    isListItem
                    key={entityFieldItemIndex}
                    userOperationEntityFieldsData={entityFieldItem}
                    entityType={isDiffItem ? entityFieldType : entityListTypeMap[entityFieldName]}
                    {
                      ...(isDiffItem ? {
                        entityFieldNamePrev: entityFieldName,
                      } : {})
                    }
                  />
                );
              })
            }
          </Fragment>
        );
      }

      return entityFieldValue.map((entityFieldValueItem: string): StructureItem => (
        FormatService.formatJsonString(entityFieldValueItem, true)
      )).join(',\n');
    }

    if (StructureService.checkIsValueObject(entityFieldValue)) {
      if (!Object.keys(entityFieldValue).length) {
        return null;
      }

      if (entityTypeMap && entityTypeMap[entityFieldName]) {
        return (
          <SettingsUserOperationFieldList
            userOperationEntityFieldsData={entityFieldValue}
            entityType={isDiffObject ? entityFieldType : entityTypeMap[entityFieldName]}
            {
              ...(isDiffObject ? {
                entityFieldNamePrev: entityFieldName,
              } : {})
            }
          />
        );
      }

      if (isListItem && entityListTypeMap && entityListTypeMap[entityFieldName]) {
        return (
          <SettingsUserOperationFieldList
            userOperationEntityFieldsData={entityFieldValue}
            entityType={entityListTypeMap[entityFieldName]}
          />
        );
      }

      if (isDiffObject) {
        return (
          <SettingsUserOperationFieldList
            userOperationEntityFieldsData={entityFieldValue}
            entityType={entityFieldType}
            entityFieldNamePrev={entityFieldName}
          />
        );
      }

      if (entityFieldValue.type) {
        if (entityFieldValue.type === FormatService.JSON_TYPE_CURRENCY) {
          return FormatService.formatCurrencyText(entityFieldValue, false, true);
        }

        if (entityFieldValue.type === FormatService.JSON_TYPE_PERCENT) {
          return FormatService.formatPercentText(entityFieldValue, true);
        }

        if (entityFieldValue.type === FormatService.JSON_TYPE_MEASURED) {
          return FormatService.formatMeasuredText(entityFieldValue, true);
        }

        if (entityFieldValue.type === FormatService.JSON_TYPE_PRICING) {
          return FormatService.formatPricingText(entityFieldValue, true);
        }
      }

      if (entityFieldValue.value !== undefined) {
        if (!entityFieldValue.value && entityFieldValue.value !== 0) {
          return null;
        }

        if (Object.keys(entityFieldValue).length === 1
          && !StructureService.checkIsValueObject(entityFieldValue.value)) {
          return entityFieldValue.value.toString();
        }

        return (
          <SettingsUserOperationFieldList
            userOperationEntityFieldsData={entityFieldValue}
            entityType={entityFieldType}
          />
        );
      }

      const isAddressValue: boolean = !!entityFieldType && !!ADDRESS_VALUE_ENTITY_MAP[entityFieldType]
        && ADDRESS_VALUE_ENTITY_MAP[entityFieldType].includes(entityFieldName);

      if (isAddressValue && entityFieldValue && GoogleMapService.checkIsValidAddress(entityFieldValue)) {
        return GoogleMapService.getFullAddress(entityFieldValue);
      }

      return FormatService.formatJsonString(entityFieldValue, true);
    }

    const isHtmlTextField: boolean = !!entityFieldType && !!HTML_TEXT_VALUE_ENTITY_MAP[entityFieldType]
      && HTML_TEXT_VALUE_ENTITY_MAP[entityFieldType].includes(entityFieldName);

    if (isHtmlTextField && entityFieldValue) {
      return FormatService.formatHtmlToComponent(entityFieldValue);
    }

    const isDateField: boolean = !!entityFieldType && !!DATE_VALUE_ENTITY_MAP[entityFieldType]
      && DATE_VALUE_ENTITY_MAP[entityFieldType].includes(entityFieldName);

    if (isDateField && entityFieldValue) {
      return DateService.formatDate(DateService.parseISODate(entityFieldValue)) as string;
    }

    const isHiddenValue: boolean = !!entityFieldType && !!HIDDEN_VALUE_ENTITY_MAP[entityFieldType]
      && HIDDEN_VALUE_ENTITY_MAP[entityFieldType].includes(entityFieldName);

    if (isHiddenValue && entityFieldValue) {
      return FormatService.formatEncryptedString(entityFieldValue, entityFieldValue.length);
    }

    return entityFieldValue.toString();
  }, []);

  const prepareFieldsData: Function = useCallback((
    entityFieldsData: Record<string, StructureItem>, entityFieldType: string,
  ): Record<string, StructureItem> => {
    const resultEntityFieldsData: Record<string, StructureItem> = { ...entityFieldsData };

    if (entityFieldsData && !!Object.keys(entityFieldsData).length) {
      Object.keys(entityFieldsData).forEach((entityFieldName: string): void => {
        const entityFieldValue: StructureItem = entityFieldsData[entityFieldName];
        const entityListTypeMap: Record<string, string> | null = (entityFieldType
          && ENTITY_LIST_TYPE_ENTITY_MAP[entityFieldType]) || null;

        if (!entityFieldValue && entityFieldValue !== 0) {
          return;
        }

        if (StructureService.checkIsValueArray(entityFieldValue)) {
          if (!entityFieldValue.length) {
            return;
          }

          if (entityListTypeMap && entityListTypeMap[entityFieldName]) {
            entityFieldValue.forEach((entityFieldItem: StructureItem, entityFieldIndex: number): void => {
              resultEntityFieldsData[entityFieldName][entityFieldIndex] = prepareFieldsData(
                entityFieldItem, entityListTypeMap[entityFieldName],
              );
            })
          }
        }

        if (StructureService.checkIsValueObject(entityFieldValue)) {
          if (!Object.keys(entityFieldValue).length) {
            return;
          }

          const isAddressValue: boolean = !!entityFieldType && !!ADDRESS_VALUE_ENTITY_MAP[entityFieldType]
            && ADDRESS_VALUE_ENTITY_MAP[entityFieldType].includes(entityFieldName);

          if (isAddressValue) {
            resultEntityFieldsData[entityFieldName] = GoogleMapService.getFullAddress(entityFieldValue);
          }
        }
      });
    }

    return resultEntityFieldsData;
  }, []);

  const onSwitchVisibleDataItem: Function = useCallback((userOperationId: number): void => {
    const newVisibleResultDataItems: number[] = visibleDataItems;

    const userOperationIdIndex: number = newVisibleResultDataItems.findIndex((userOperationItemId: number): boolean => (
      userOperationItemId === userOperationId
    ));

    if (userOperationIdIndex !== FormatService.INDEX_NOT_FOUND) {
      StructureService.deleteListItemByIndex(newVisibleResultDataItems, userOperationIdIndex);
    } else {
      newVisibleResultDataItems.push(userOperationId);
    }

    setVisibleDataItems([...newVisibleResultDataItems]);
  }, [visibleDataItems]);

  const entityFieldMap: Record<string, StructureItem> = (
    isDiff && userOperationEntityFieldsData && userOperationNewEntityFieldsData ? StructureService.calculateDiff(
      prepareFieldsData(userOperationEntityFieldsData, entityType),
      prepareFieldsData(userOperationNewEntityFieldsData, entityType),
    ) : userOperationEntityFieldsData
  );

  useEffect((): ReactUseEffectCleanUp => (): void => {
    if (isRoot) {
      setVisibleDataItems([]);
    }
  }, []);

  return (
    <Fragment>
      {
        isRoot && entityFieldMap && !!Object.keys(entityFieldMap).length && (
          <FilteredLink
            isSwitcher
            onClick={(): void => {
              onSwitchVisibleDataItem(userOperationId);
            }}
          >
            {visibleDataItems.includes(userOperationId) ? 'Hide' : 'Show'}
            {' '}
            Fields
          </FilteredLink>
        )
      }
      {
        entityFieldMap && !!Object.keys(entityFieldMap).length
        && ((isRoot && visibleDataItems.includes(userOperationId)) || !isRoot) && (
          <Table>
            <TableBody withNoLastBorder={isRoot} withAllBorderCells={!isRoot}>
              {
                Object.keys(entityFieldMap).map((
                  entityFieldName: string, entityFieldIndex: number,
                ): ReactElement | boolean => {
                  const isHiddenField: boolean = entityType && HIDDEN_FIELD_ENTITY_MAP[entityType]
                    && HIDDEN_FIELD_ENTITY_MAP[entityType].includes(entityFieldName);
                  const isHeaderField: boolean = entityFieldName === ENTITY_FIELD_NAME_ORIGINAL
                    || entityFieldName === ENTITY_FIELD_NAME_RESULT;
                  const isKeyToFlat: boolean = entityType && FLAT_KEY_ENTITY_LIST_MAP[entityType]
                    && FLAT_KEY_ENTITY_LIST_MAP[entityType].includes(entityFieldName);
                  const entityFieldValue: StructureItem = entityFieldMap[entityFieldName];
                  const resultEntityFieldName: string = entityFieldNamePrev || entityFieldName;

                  const formattedEntityFieldName: string = formatEntityFieldName(
                    entityFieldName, entityType,
                  );
                  const formattedValue: string = formatEntityFieldValue(
                    entityFieldValue, resultEntityFieldName, entityType,
                  );

                  if (isKeyToFlat && StructureService.checkIsValueObject(entityFieldValue)
                    && ENTITY_TYPE_ENTITY_MAP[entityType][entityFieldName]) {
                    return (
                      <TableRow key={entityFieldIndex}>
                        <TableCell colSpan={COLUMNS_TOTAL}>
                          <SettingsUserOperationFieldList
                            userOperationEntityFieldsData={entityFieldValue}
                            entityType={ENTITY_TYPE_ENTITY_MAP[entityType][entityFieldName]}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return !!formattedEntityFieldName && !!formattedValue && !isHiddenField && (
                    <TableRow key={entityFieldIndex}>
                      <TableCell isTextNoWrap withSmallPadding isHeader={isHeaderField}>
                        {formattedEntityFieldName}
                      </TableCell>
                      <TableCell withSmallPadding>
                        {formattedValue}
                      </TableCell>
                    </TableRow>
                  );
                })
              }
            </TableBody>
          </Table>
        )
      }
    </Fragment>
  );
});

export default SettingsUserOperationFieldList;
